import React from 'react';
import { useAppContext } from 'src/components/app-context';
import type { DescriptionSlice, ProductCollection } from '../../../../types';
import UserProductFeedback from '../user-product-feedback';
import * as S from './styles';
import {
  DescriptionBox,
  DescriptionContainer,
  DescriptionText,
  DescriptionTitle,
} from './styles';

type Props = {
  productCollection: ProductCollection;
} & Pick<DescriptionSlice, 'description'>;

const Description = ({ productCollection, description }: Props) => {
  const { t, linkTo, market = { code: '' } } = useAppContext();
  const feedbackEnabled = market.code.toLocaleLowerCase() === 'se';

  const scrollToTop = React.useCallback(() => window.scroll(0, 0), []);
  const aliases: React.ReactNode = React.useMemo(() => {
    const categoryUrl = linkTo('productCollection', {
      pathName: productCollection.url,
      filters: productCollection.filters,
    });

    const arr =
      productCollection.aliases?.filter(
        // We don't want empty aliases
        alias => alias.trim()
      ) ?? [];
    const links = arr.map((alias, index) => (
      <React.Fragment key={alias}>
        <S.AliasLink to={categoryUrl} onClick={scrollToTop}>
          {alias}
        </S.AliasLink>
        {arr.length > 2 && index < arr.length - 2 && <>, </>}
      </React.Fragment>
    ));

    if (links.length === 1) {
      return t('category', 'product-collection.aliases-single', {
        alias: links[0],
      });
    }

    if (links.length > 1) {
      return t('category', 'product-collection.aliases-plural', {
        aliasesExceptLast: links.slice(0, links.length - 1),
        last: links[links.length - 1],
      });
    }

    return null;
  }, [productCollection, linkTo, t]);

  if (!description && !aliases && feedbackEnabled)
    return (
      <DescriptionContainer>
        <UserProductFeedback />
      </DescriptionContainer>
    );

  const descriptionText = () => {
    if (!description) return aliases;
    if (!aliases) return description;
    return (
      <>
        {description} {aliases}
      </>
    );
  };

  return (
    <DescriptionContainer>
      <DescriptionBox>
        <DescriptionTitle>{productCollection.name}</DescriptionTitle>
        <DescriptionText data-test="ProductCollectionDescription">
          {descriptionText()}
        </DescriptionText>
      </DescriptionBox>
      {feedbackEnabled ? <UserProductFeedback /> : null}
    </DescriptionContainer>
  );
};

export default Description;
